<template>
    <div class="p-6">
        <div class="grid grid-cols-3 gap-6">
            <div class="col-span-2">
                <section-panel heading="Items">
                    <table class="w-full">
                        <tr>
                            <th class="font-medium text-left px-4 pb-4">Item</th>
                            <th class="w-px font-medium text-right px-4 pb-4">Price</th>
                            <th class="w-px font-medium text-center px-4 pb-4">Quantity</th>
                            <th class="w-px font-medium text-right px-4 pb-4">Subtotal</th>
                        </tr>
                        <template v-for="item in order.items">
                            <tr class="border-t border-gray-200 first:border-0">
                                <td class="text-left px-4 pt-4 align-top">
                                    <div class="flex items-start">
                                        <div>
                                            <div class="font-semibold uppercase mb-1 -mt-1 pb-2">
                                                {{ item.name }}
                                            </div>
                                            <div class="text-gray-500 text-sm font-medium leading-5 pb-2"
                                                 v-if="item.is_event"
                                            >
                                                <div>Date: {{ item.event_date.start_date_no_time }}</div>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td class="text-right px-4 pt-4 align-top">£{{ item.formatted_price }}</td>
                                <td class="text-center px-4 pt-4 align-top">
                                    <div>{{ item.quantity }}</div>
                                </td>
                                <td class="text-right px-4 pt-4 align-top">£{{ item.formatted_total }}</td>
                            </tr>
                            <tr class="text-sm text-gray-500"
                                v-for="subItem in item.sub_items"
                            >
                                <td class="text-left px-4 pb-2 align-top">
                                    <div class="flex items-start">
                                        <div>
                                            <div class="mb-1 -mt-1">
                                                {{ subItem.name }}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td class="text-right px-4 pb-2 align-top whitespace-no-wrap">+£{{ subItem.formatted_price }}</td>
                                <td class="text-center px-4 pb-2 align-top">
                                    {{ subItem.quantity }}
                                </td>
                                <td class="text-right px-4 pb-2 align-top whitespace-no-wrap">+£{{ subItem.formatted_total }}</td>
                            </tr>
                        </template>
                    </table>

                    <table class="w-full">
                        <tr v-for="charge in order.formatted_additional_charges">
                            <td class="px-4 py-2 align-top border-t border-gray-100" colspan="3">
                                {{ charge.name }}
                            </td>
                            <td class="text-right px-4 py-2 align-top border-t border-gray-100">£{{ charge.formatted_amount }}</td>
                        </tr>

                        <tr v-if="order.membership_discount > 0">
                            <td class="px-4 py-2 align-top border-t border-gray-100" colspan="3">
                                Membership Discount
                            </td>
                            <td class="text-right px-4 py-2 align-top border-t border-gray-100">- £{{ order.formatted_membership_discount }}</td>
                        </tr>

                        <tr v-if="order.credit_transaction">
                            <td class="px-4 py-2 align-top border-t border-gray-100" colspan="3">
                                Account Credit
                            </td>
                            <td class="text-right px-4 py-2 align-top border-t border-gray-100">
                                - £{{ order.credit_transaction.formatted_amount.replace('-', '') }}
                            </td>
                        </tr>

                        <tr>
                            <td class="px-4 py-2 align-top border-t border-dashed border-gray-100 font-semibold" colspan="3">
                                Total
                            </td>
                            <td class="text-right px-4 py-2 align-top border-t border-b border-gray-100 font-semibold">£{{ orderTotal }}</td>
                        </tr>
                    </table>
                </section-panel>

                <section-panel heading="Vouchers"
                               class="mt-6"
                               v-if="vouchers.length > 0"
                >
                    <table class="w-full">
                        <tr>
                            <th class="font-medium text-left px-4 pb-4">Code</th>
                            <th class="w-px font-medium text-right px-4 pb-4">Amount</th>
                            <th class="w-px font-medium text-center px-4 pb-4">Status</th>
                        </tr>
                        <tr class="border-t border-gray-200 first:border-0"
                            v-for="voucher in vouchers"
                        >
                            <td class="text-left px-4 pt-4 align-top">{{ voucher.hyphenated_code }}</td>
                            <td class="right px-4 pt-4 align-top">£{{ voucher.formatted_amount }}</td>
                            <td class="text-left px-4 pt-4 align-top whitespace-no-wrap">
                                <badge :color="voucher.has_been_redeemed ? 'green' : 'red'"
                                       class="ml-3 whitespace-no-wrap"
                                >
                                    {{ voucher.has_been_redeemed ? 'Redeemed' : 'Un-redeemed' }}
                                </badge>
                            </td>
                        </tr>
                    </table>
                </section-panel>

                <h4 class="text-lg leading-6 text-gray-900 px-4 py-5 mt-6 mb-5 border-b border-gray-200"
                    v-if="order.notes.length > 0"
                >
                    Notes
                </h4>

                <div class="mt-10">
                    <div class="border-b border-gray-200 last:border-0"
                         v-for="note in order.notes"
                    >
                        <div class="grid grid-cols-4 gap-10 text-sm p-4">
                            <div class="col-span-3">
                                <p class="font-semibold text-primary-600 mb-1"
                                   v-if="note.notify_customer"
                                >
                                    Customer Notified
                                </p>
                                <p v-html="nl2br(note.body)"></p>
                            </div>

                            <div class="text-right">
                                <div>{{ note.formatted_created_date }}</div>
                                <div class="font-medium">{{ note.made_by.name }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div>
                <description-list heading="Billing Information"
                                  :list-items="[
                                              { name: 'Name', value: `${order.billing_first_name} ${order.billing_last_name}` },
                                              { name: 'Address', value: `${order.billing_address_1}<br>${order.billing_address_2}<br>${order.billing_town}<br>${order.billing_county}<br>${order.billing_post_code}` },
                                          ]"
                ></description-list>

                <description-list heading="Contact Information"
                                  class="mt-6"
                                  :list-items="[
                                              { name: 'Email', value: order.customer.email },
                                              { name: 'Phone', value: order.customer.phone },
                                          ]"
                ></description-list>
            </div>
        </div>
    </div>

<!--    <submenu-layout>-->

<!--        <template slot="footer">-->
<!--            <div class="px-6 pb-6">-->
<!--                <div class="flex mt-8 border-t border-gray-100 pt-4">-->
<!--                    <form-button colour="white"-->
<!--                                 :href="route('customer.order.index')"-->
<!--                    >-->
<!--                        Back-->
<!--                    </form-button>-->
<!--                </div>-->
<!--            </div>-->
<!--        </template>-->
<!--    </submenu-layout>-->
</template>

<script>
    import DescriptionList from "../../../components/DescriptionList";
    import SectionPanel from "../../../components/Panel";
    import Badge from "../../../components/Badge";
    import FormButton from "../../../components/form/Button";
    import nl2br from "../../../mixins/nl2br";

    export default {
        props: {
            order: {
                type: Object,
                required: true,
            },

            orderTotal: {
                type: String,
                required: true,
            },

            vouchers: {
                type: Array,
            }
        },

        components: {
            DescriptionList,
            SectionPanel,
            Badge,
            FormButton,
        },

        mixins: [nl2br],

        computed: {
            orderStatusColour() {
                switch (this.order.status) {
                    case 1:
                        return 'green';

                    case 2:
                        return 'gray';

                    default:
                        return 'red';
                }
            },

            orderStatusText() {
                switch (this.order.status) {
                    case 1:
                        return 'Fulfilled';

                    case 2:
                        return 'Cancelled';

                    default:
                        return 'Unfulfilled';
                }
            },

            orderStatusButtonColour() {
                switch (this.order.status) {
                    case 1:
                        return 'red';

                    default:
                        return 'primary';
                }
            },

            orderStatusButtonText() {
                switch (this.order.status) {
                    case 1:
                        return 'Unfulfilled';

                    default:
                        return 'Fulfilled';
                }
            },
        },
    }
</script>
